import React, { useState, useRef, useEffect, useMemo } from 'react';
import './ai-sidebar.css';
import { useTranslation } from 'react-i18next';
import {
    RiArrowUpLine,
    RiArrowUpSLine,
    RiArrowDownSLine,
    RiExternalLinkLine,
    RiThumbDownLine,
    RiThumbUpLine,
    RiThumbUpFill,
    RiThumbDownFill,
} from '@remixicon/react';
import { cn } from '~/helpers/tw';
// eslint-disable-next-line import/no-cycle
import { WEBAPP_ROUTE_PREFIX } from '~/constants';
import useMediaQuery from '~/hooks/useMediaQuery';
import { useLocation, useRouteLoaderData } from 'react-router';
import { useGetIntegrationByIdQuery } from '~/hasura/__generated__';
import type { loader } from '~/root';
import { AiButton } from './ai-button';

interface MessagePair {
    id: string;
    question: string;
    answer: string;
    vote: true | false | null;
    idLog?: string;
    error?: string;
    thinking?: boolean;
}

interface StoredState {
    messages: MessagePair[];
    open: boolean;
    minimized: boolean;
}

interface AiSidebarProps {
    username?: string;
    noMenu?: boolean;
}

export const SESSION_STORAGE_KEY = 'naviaAIConversation';
export const AV_ENABLED_KEY = 'naviaAVEnabled';

const AI_INTEGRATION_ID = 33;

// Rotas onde o botão do AV não deve aparecer (primeiro segmento do pathname)
const AI_BUTTON_EXCLUDED_ROUTES = new Set(['login', 'not-authorized']);

// Regra especial: dashboard individual (não o hub)
function isDashboardIndividual(pathnames: string[]) {
    return (
        pathnames[0] === 'dashboards' &&
        pathnames[1] === 'visioon' &&
        pathnames.length > 2
    );
}

function getStoredConversation(): MessagePair[] {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) return parsed;
            if (parsed && Array.isArray(parsed.messages))
                return parsed.messages;
        } catch {
            return [];
        }
    }
    return [];
}

function getStoredState(): StoredState | null {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            if (
                parsed &&
                !Array.isArray(parsed) &&
                Array.isArray(parsed.messages)
            ) {
                return parsed as StoredState;
            }
        } catch {
            return null;
        }
    }
    return null;
}

const AiSidebar: React.FC<AiSidebarProps> = ({ username, noMenu }) => {
    const isLargeDevice = useMediaQuery('(min-width: 80rem)');
    const { isLoggedIn } = useRouteLoaderData<typeof loader>('root') ?? {};
    const location = useLocation();
    const pathnames = useMemo(
        () =>
            location.pathname
                .split('/')
                .filter(Boolean)
                .map((s) => s.toLowerCase()),
        [location.pathname]
    );

    const { data: dataAI } = useGetIntegrationByIdQuery({
        variables: { id: AI_INTEGRATION_ID },
        skip: !isLoggedIn,
    });
    const isAiEnabled = dataAI?.navia_integracoes[0]?.activo ?? false;

    const isExcludedRoute =
        AI_BUTTON_EXCLUDED_ROUTES.has(pathnames[0]) ||
        isDashboardIndividual(pathnames);

    const [open, setOpen] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            const storedState = getStoredState();
            if (storedState) return storedState.open === true;
            // Legacy array format: open if has messages
            return getStoredConversation().length > 0;
        }
        return false;
    });
    const [minimized, setMinimized] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            const storedState = getStoredState();
            return storedState?.minimized === true;
        }
        return false;
    });
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { t } = useTranslation();
    const [insertOnTop] = useState(false);
    const [questionPositionRight] = useState(true);
    const [showFullQuestionOnTitle] = useState(true);
    const TEMPERATURE = 0.2;
    const SYSTEM_PROMPT = 'You are a helpful assistant for NAVIA.';
    const TOP_P = 0.9;
    const MAX_NEW_TOKENS = 256;
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!loading && inputRef.current) {
            inputRef.current.focus();
        }
    }, [loading]);

    function writeStoredState(
        messages: MessagePair[],
        isOpen: boolean,
        isMinimized: boolean
    ) {
        sessionStorage.setItem(
            SESSION_STORAGE_KEY,
            JSON.stringify({ messages, open: isOpen, minimized: isMinimized })
        );
    }

    // Generate a simple UUID
    function generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.floor(Math.random() * 16);
            // eslint-disable-next-line no-bitwise
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    // Conversation state
    const [conversation, setConversation] = useState<MessagePair[]>(() => {
        if (typeof window !== 'undefined') {
            return getStoredConversation();
        }
        return [];
    });

    // Persist conversation + ui state to sessionStorage
    useEffect(() => {
        writeStoredState(conversation, open, minimized);
    }, [conversation, open, minimized]);

    const prevLastMessageId = useRef<string | undefined>();
    const prevLastAnswer = useRef<string | undefined>();
    const prevLastError = useRef<string | undefined>();

    useEffect(() => {
        if (!insertOnTop && messagesEndRef.current && conversation.length > 0) {
            const lastMessage = conversation[conversation.length - 1];
            const lastAnswerChanged =
                prevLastAnswer.current !== lastMessage.answer;
            const lastErrorChanged =
                prevLastError.current !== lastMessage.error;
            const isNewMessage = prevLastMessageId.current !== lastMessage.id;

            if (lastAnswerChanged || lastErrorChanged || isNewMessage) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }

            prevLastMessageId.current = lastMessage.id;
            prevLastAnswer.current = lastMessage.answer;
            prevLastError.current = lastMessage.error;
        }
    }, [conversation, insertOnTop]);

    useEffect(() => {
        if (!isLargeDevice && open) {
            setOpen(false);
        }
    }, [isLargeDevice, open]);

    useEffect(() => {
        if (!dropdownOpen) return undefined;
        const handleClickOutside = () => setDropdownOpen(false);
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [dropdownOpen]);

    // Derive firstName from username with edge case handling
    const trimmedUsername = username ? username.trim() : '';
    const firstName =
        trimmedUsername.length > 0 ? trimmedUsername.split(' ')[0] : null;
    const isLongName = firstName !== null && firstName.length > 15;
    const fontClass = isLongName ? 'text-[20px]' : 'text-[26px]';

    // Renders the question text (truncated or full)
    function renderQuestionText(question: string) {
        const truncateClass = !showFullQuestionOnTitle
            ? 'overflow-hidden text-ellipsis whitespace-nowrap'
            : '';

        return (
            <span
                className={cn(
                    'font-roboto break-words text-sm text-gray-800 dark:text-[#E1E4E8]',
                    questionPositionRight
                        ? 'text-right'
                        : 'h-[18px] w-[294px] text-left',
                    truncateClass
                )}
                style={{ overflowWrap: 'anywhere' }}
                title={!showFullQuestionOnTitle ? question : undefined}
            >
                {question}
            </span>
        );
    }

    // Submit handler
    const handleSubmit = async (e?: React.FormEvent | React.KeyboardEvent) => {
        if (e) e.preventDefault();
        if (!input.trim() || loading) return;

        const userQuestion = input.trim();
        const newId = generateUUID();

        setConversation((prev) =>
            insertOnTop
                ? [
                      {
                          id: newId,
                          question: userQuestion,
                          answer: '',
                          vote: null,
                          error: '',
                          thinking: true,
                      },
                      ...prev,
                  ]
                : [
                      ...prev,
                      {
                          id: newId,
                          question: userQuestion,
                          answer: '',
                          vote: null,
                          error: '',
                          thinking: true,
                      },
                  ]
        );
        setInput('');
        setLoading(true);

        if (inputRef.current) inputRef.current.blur();

        try {
            const response = await fetch(
                `${WEBAPP_ROUTE_PREFIX}/api/av/chat-stream`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        question: userQuestion,
                        system: SYSTEM_PROMPT,
                        temperature: TEMPERATURE,
                        top_p: TOP_P,
                        max_new_tokens: MAX_NEW_TOKENS,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) throw new Error('Server has no response');

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let done = false;
            let fullAnswer = '';
            let idLog: string | undefined;
            let isFirstChunk = true;

            let buffer = '';
            const processStreamRecursively = async () => {
                try {
                    const result = await reader.read();

                    if (result.done || done) {
                        return;
                    }

                    buffer += decoder.decode(result.value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    const parsedData = lines
                        .filter((line) => line.trim())
                        .map((line) => {
                            try {
                                return JSON.parse(line);
                            } catch {
                                return null;
                            }
                        })
                        .filter((parsed) => parsed !== null);

                    const processed = parsedData.reduce(
                        (acc, parsed) => {
                            if (acc.shouldStop) return acc;

                            const chunkText = parsed.chunk || '';
                            if (
                                parsed.idLog !== undefined &&
                                parsed.idLog !== null
                            ) {
                                acc.idLog = String(parsed.idLog);
                            }

                            const cleanChunk = chunkText.replace(
                                /__TTFB__:[^\n\r]*/g,
                                ''
                            );

                            // Note: if an error < 0 is received, print the default message
                            if (
                                parsed.error !== undefined &&
                                typeof parsed.error === 'number' &&
                                parsed.error < 0
                            ) {
                                acc.shouldStop = true;
                                acc.hasError = true;
                                return acc;
                            }

                            // Check chunk __DONE__ to finalize
                            if (cleanChunk.includes('__DONE__')) {
                                acc.shouldStop = true;
                                return acc;
                            }

                            acc.fullAnswer += cleanChunk;
                            if (cleanChunk.trim()) acc.chunks.push(cleanChunk);

                            return acc;
                        },
                        {
                            shouldStop: false,
                            hasError: false,
                            idLog,
                            fullAnswer,
                            chunks: [],
                        }
                    );

                    // Update outer scope variables
                    idLog = processed.idLog;
                    fullAnswer = processed.fullAnswer;

                    if (processed.hasError) {
                        done = true;
                        setConversation((prev) =>
                            prev.map((item) =>
                                item.id === newId
                                    ? {
                                          ...item,
                                          answer: '',
                                          idLog: processed.idLog,
                                          vote: null,
                                          error: t('aiAssistant.errorDefault'),
                                          thinking: false,
                                      }
                                    : item
                            )
                        );
                        return;
                    }

                    if (processed.shouldStop) {
                        done = true;
                        return;
                    }

                    // Update conversation with chunks
                    if (processed.chunks.length > 0) {
                        const firstChunk = processed.chunks[0];
                        if (isFirstChunk) {
                            setConversation((prev) =>
                                prev.map((item) =>
                                    item.id === newId
                                        ? {
                                              ...item,
                                              answer: firstChunk,
                                              error: '',
                                              thinking: false,
                                          }
                                        : item
                                )
                            );
                            isFirstChunk = false;
                        } else {
                            setConversation((prev) =>
                                prev.map((item) =>
                                    item.id === newId
                                        ? {
                                              ...item,
                                              answer: processed.fullAnswer,
                                              error: '',
                                              thinking: false,
                                          }
                                        : item
                                )
                            );
                        }
                    }

                    // Recursive call instead of loop
                    if (!done) {
                        await processStreamRecursively();
                    }
                } catch (streamError) {
                    done = true;
                    throw streamError; // Re-throw to be caught by the outer catch
                }
            };

            // Start the recursive processing
            await processStreamRecursively();

            const finalAnswer = fullAnswer.trim();
            setConversation((prev) =>
                prev.map((item) =>
                    item.id === newId
                        ? {
                              ...item,
                              answer: finalAnswer,
                              idLog,
                              vote: null,
                              error: '',
                              thinking: false,
                          }
                        : item
                )
            );
        } catch (err: unknown) {
            let errorMsg = t('aiAssistant.errorDefault');
            if (
                !errorMsg ||
                typeof errorMsg !== 'string' ||
                errorMsg === 'aiAssistant.errorDefault' ||
                errorMsg.trim() === ''
            ) {
                errorMsg =
                    'Unable to respond at the moment, please try again later.';
            }
            setConversation((prev) =>
                prev.map((item) =>
                    item.id === newId
                        ? {
                              ...item,
                              answer: '',
                              vote: null,
                              error: errorMsg,
                              thinking: false,
                          }
                        : item
                )
            );
        } finally {
            setLoading(false);
        }
    };

    const handleTextareaKeyDown = (
        e: React.KeyboardEvent<HTMLTextAreaElement>
    ) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    // Minimize: keep messages, close panel but show button
    const handleMinimize = () => {
        setOpen(false);
        setMinimized(true);
    };

    // Close: clear all state
    const handleClose = () => {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        setConversation([]);
        setInput('');
        setOpen(false);
        setMinimized(false);
    };

    const handleFeedback = async (
        id: string,
        idLog: string | undefined,
        newVote: true | false | null
    ) => {
        setConversation((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, vote: newVote } : item
            )
        );

        if (idLog) {
            try {
                await fetch(
                    `${WEBAPP_ROUTE_PREFIX}/api/av/feedback?idLog=${encodeURIComponent(idLog)}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ feedback: newVote }),
                    }
                );
            } catch (err) {
                // Ignore errors
            }
        }
    };

    if (!isAiEnabled || isExcludedRoute) {
        return null;
    }

    if (!isLargeDevice) {
        return null;
    }

    // Border color: use Tailwind only if palette, else keep style
    if (!open) {
        return (
            <AiButton
                className="navia-ai-open-btn text-white"
                onClick={() => {
                    setOpen(true);
                    setMinimized(false);
                }}
            />
        );
    }

    return (
        <div
            className={cn(
                'navia-ai-assistant-root font-roboto rounded-xl fixed right-0 z-[10001] flex w-[380px] max-w-full flex-col overflow-hidden border bg-white dark:bg-[#3A444F]',
                noMenu
                    ? 'top-0 h-full'
                    : `top-[var(--menu-height,83px)] h-[calc(100vh-var(--menu-height,83px))]`
            )}
            style={{
                boxShadow: '-8px 4px 92px 0px #00000040',
                borderColor: 'rgb(246 247 249)',
                borderRadius: 12,
            }}
        >
            {/* Header — outside scroll */}
            <div className="flex h-10 w-full flex-shrink-0 flex-row items-center justify-end border-gray-200 bg-white px-5 dark:bg-[#3A444F]">
                <div id="header-menu" className="relative flex items-center">
                    <button
                        id="menu-toggle"
                        aria-label={t('aiAssistant.menu')}
                        aria-haspopup="true"
                        aria-expanded={dropdownOpen}
                        onClick={(e) => {
                            e.stopPropagation();
                            setDropdownOpen((v) => !v);
                        }}
                        className="rounded flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-[#185490] dark:text-white"
                    >
                        {dropdownOpen ? (
                            <RiArrowDownSLine size={24} />
                        ) : (
                            <RiArrowUpSLine size={24} />
                        )}
                    </button>
                    {dropdownOpen && (
                        <ul
                            role="menu"
                            className="navia-ai-dropdown absolute right-0 z-[100] m-0 list-none overflow-hidden rounded-md border border-gray-200 bg-white p-0 dark:border-[#596879] dark:bg-[#3A444F]"
                            style={{
                                top: 'calc(100% + 4px)',
                                width: 107,
                                boxSizing: 'border-box',
                                boxShadow:
                                    '0 2px 4px -2px rgba(0,0,0,0.20), 0 4px 6px -1px rgba(0,0,0,0.20)',
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                        >
                            <li role="none" className="hidden">
                                <button
                                    role="menuitem"
                                    className="flex w-full cursor-pointer items-center gap-2 self-stretch border-none bg-transparent normal-case transition-[background-color] duration-[150ms] ease-linear"
                                    style={{
                                        height: 29,
                                        padding: '0 12px 0 20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <span className="font-[Arial] text-[14px] font-normal leading-[150%] text-[#0A0A0A] dark:text-[#F6F7F9]">
                                        {t('aiAssistant.expandLabel')}
                                    </span>
                                </button>
                            </li>
                            <li role="none">
                                <button
                                    role="menuitem"
                                    onClick={() => {
                                        handleMinimize();
                                        setDropdownOpen(false);
                                    }}
                                    className="flex w-full cursor-pointer items-center gap-2 self-stretch border-none bg-transparent normal-case"
                                    style={{
                                        height: 29,
                                        padding: '0 12px 0 20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    // TODO: hover bg-gray-50 disabled — pending design decision
                                    // onMouseEnter={e => (e.currentTarget.style.backgroundColor = document.documentElement.dataset.theme === 'dark' ? '#303841' : '#f9fafb')}
                                    // onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <span className="font-[Arial] text-[14px] font-normal leading-[150%] text-[#0A0A0A] dark:text-[#F6F7F9]">
                                        {t('aiAssistant.minimizeLabel')}
                                    </span>
                                </button>
                            </li>
                            <li role="none">
                                <button
                                    role="menuitem"
                                    onClick={() => {
                                        handleClose();
                                        setDropdownOpen(false);
                                    }}
                                    className="group flex w-full cursor-pointer items-center gap-2 self-stretch border-none bg-transparent normal-case"
                                    style={{
                                        height: 29,
                                        padding: '0 12px 0 20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        transition:
                                            'background-color 150ms linear',
                                    }}
                                >
                                    {/* TODO: hover bg-gray-50 disabled — pending design decision */}
                                    <span className="font-[Arial] text-[14px] font-normal leading-[150%] text-[#0A0A0A] group-hover:text-red-700 dark:text-[#F6F7F9] dark:group-hover:text-red-200">
                                        {t('aiAssistant.endChatLabel')}
                                    </span>
                                </button>
                            </li>
                        </ul>
                    )}
                </div>
            </div>

            {/* Content — scrollable */}
            <div className="flex flex-1 flex-col items-center overflow-y-auto px-5 pt-[10px]">
                {conversation.length === 0 ? (
                    <>
                        <div className="welcome-message flex h-full flex-col items-center justify-start gap-2 px-5 text-center text-[#3a444f] dark:text-[#C7CED6]">
                            <div className="greeting-row flex flex-row items-center gap-2">
                                <div
                                    className="welcome-avatar flex-shrink-0 rounded-full"
                                    style={{
                                        width: 38,
                                        height: 38,
                                        boxShadow:
                                            '1.41px 2.81px 7.04px -2.11px rgba(0,0,0,0.33)',
                                    }}
                                >
                                    <svg
                                        width="38"
                                        height="38"
                                        viewBox="0 0 54 54"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <rect
                                            width="54"
                                            height="54"
                                            rx="27"
                                            className="fill-blue-500 dark:fill-[#AFC8E0]"
                                        />
                                        <path
                                            d="M24.276 20.549 C24.8851 18.7666 27.3479 18.7126 28.07 20.387 L28.1311 20.55 L28.9531 22.9537 C29.1414 23.5049 29.4458 24.0094 29.8457 24.433 C30.2456 24.8566 30.7317 25.1895 31.2712 25.4093 L31.4922 25.4918 L33.8959 26.3128 C35.6783 26.9218 35.7323 29.3846 34.0589 30.1067 L33.8959 30.1679 L31.4922 30.9898 C30.9408 31.178 30.4361 31.4824 30.0123 31.8823 C29.5885 32.2822 29.2555 32.7683 29.0356 33.3079 L28.9531 33.5279 L28.1321 35.9327 C27.5231 37.7151 25.0603 37.7691 24.3392 36.0956 L24.276 35.9327 L23.4551 33.529 C23.2669 32.9775 22.9625 32.4729 22.5626 32.0491 C22.1627 31.6253 21.6766 31.2922 21.137 31.0723 L20.917 30.9898 L18.5132 30.1689 C16.7298 29.5598 16.6758 27.097 18.3503 26.3759 L18.5132 26.3128 L20.917 25.4918 C21.4682 25.3035 21.9727 24.9991 22.3963 24.5992 C22.8199 24.1993 23.1528 23.7132 23.3726 23.1737 L23.4551 22.9537 L24.276 20.549 Z M26.2041 21.2069 L25.3832 23.6106 C25.0963 24.4512 24.6297 25.2192 24.0158 25.861 C23.4019 26.5029 22.6555 27.0032 21.8285 27.3272 L21.5739 27.4199 L19.1702 28.2408 L21.5739 29.0617 C22.4145 29.3486 23.1824 29.8152 23.8243 30.4291 C24.4662 31.043 24.9665 31.7894 25.2905 32.6164 L25.3832 32.871 L26.2041 35.2747 L27.025 32.871 C27.3118 32.0304 27.7784 31.2624 28.3923 30.6206 C29.0062 29.9787 29.7527 29.4784 30.5796 29.1544 L30.8343 29.0628 L33.238 28.2408 L30.8343 27.4199 C29.9937 27.1331 29.2257 26.6665 28.5839 26.0526 C27.942 25.4387 27.4417 24.6922 27.1177 23.8653 L27.026 23.6106 L26.2041 21.2069 Z M34.3522 17.0371 C34.5428 17.0371 34.7295 17.0906 34.8912 17.1914 C35.0529 17.2922 35.183 17.4364 35.2669 17.6075 L35.3157 17.7266 L35.6722 18.7716 L36.7182 19.1281 C36.9092 19.193 37.0766 19.3131 37.1992 19.4732 C37.3218 19.6333 37.3942 19.8262 37.4071 20.0275 C37.4199 20.2288 37.3728 20.4293 37.2715 20.6037 C37.1703 20.7782 37.0196 20.9186 36.8384 21.0073 L36.7182 21.0562 L35.6733 21.4127 L35.3168 22.4587 C35.2518 22.6496 35.1316 22.8169 34.9714 22.9394 C34.8112 23.0619 34.6183 23.1342 34.417 23.1469 C34.2158 23.1597 34.0153 23.1124 33.8409 23.0111 C33.6666 22.9098 33.5262 22.759 33.4376 22.5779 L33.3887 22.4587 L33.0322 21.4137 L31.9862 21.0572 C31.7953 20.9923 31.6279 20.8722 31.5052 20.7121 C31.3826 20.552 31.3103 20.3591 31.2974 20.1578 C31.2845 19.9566 31.3317 19.756 31.4329 19.5816 C31.5342 19.4072 31.6849 19.2667 31.866 19.178 L31.9862 19.1291 L33.0312 18.7727 L33.3877 17.7266 C33.4564 17.5254 33.5863 17.3507 33.7593 17.2271 C33.9323 17.1034 34.1396 17.037 34.3522 17.0371 Z"
                                            className="fill-white dark:fill-gray-800"
                                        />
                                    </svg>
                                </div>
                                <div
                                    className={cn(
                                        fontClass,
                                        'font-bold leading-none tracking-[0] text-[#3A8DDE] dark:text-[#B0D1F2]'
                                    )}
                                >
                                    {t('aiAssistant.startIntroHello')}
                                    {firstName ? `, ${firstName}` : ''}
                                </div>
                            </div>
                            <div
                                className="title_help text-center font-normal text-[#3A444F] dark:text-[#D1D5DB]"
                                style={{
                                    fontSize: 18,
                                    lineHeight: 1.5,
                                    height: 45,
                                }}
                            >
                                {t('aiAssistant.startIntroHowCanHelp')}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className={cn('flex w-full flex-col items-center')}>
                        {(insertOnTop ? conversation : [...conversation]).map(
                            (item, idx, arr) => {
                                let content = null;

                                if (item.thinking) {
                                    content = (
                                        <div
                                            className={cn(
                                                'rounded-xl font-roboto w-[300px] max-w-[300px] break-words bg-white pb-1 pr-[13px] pt-[16px] text-left text-sm text-gray-800 dark:bg-gray-800 dark:text-[#E5E7EB]'
                                            )}
                                            style={{
                                                minWidth:
                                                    '-webkit-fill-available',
                                            }}
                                        >
                                            {t('aiAssistant.thinking')}
                                        </div>
                                    );
                                } else if (item.answer || item.error) {
                                    content = (
                                        <>
                                            <div
                                                className={cn(
                                                    'rounded-xl font-roboto w-[300px] max-w-[300px] break-words bg-white pb-1 pr-[13px] pt-[16px] text-left text-sm text-gray-800 dark:bg-gray-800 dark:text-[#E5E7EB]'
                                                )}
                                                style={{
                                                    minWidth:
                                                        '-webkit-fill-available',
                                                }}
                                            >
                                                {item.error ? (
                                                    <span>{item.error}</span>
                                                ) : (
                                                    <span>{item.answer}</span>
                                                )}
                                            </div>
                                            {/* Feedback Buttons */}
                                            {item.idLog && !item.error && (
                                                <div className="mb-4 flex items-center self-start pl-[13px]">
                                                    {/* Thumbs Up */}
                                                    <button
                                                        className={cn(
                                                            'thumbs-button transition-colors',
                                                            item.vote === true
                                                                ? 'text-gray-600 dark:text-gray-600'
                                                                : 'text-black hover:text-gray-600 dark:text-white'
                                                        )}
                                                        onClick={() => {
                                                            const nextVote =
                                                                item.vote ===
                                                                true
                                                                    ? null
                                                                    : true;
                                                            handleFeedback(
                                                                item.id,
                                                                item.idLog,
                                                                nextVote
                                                            );
                                                        }}
                                                    >
                                                        {item.vote === true ? (
                                                            <RiThumbUpFill
                                                                size={13}
                                                            />
                                                        ) : (
                                                            <RiThumbUpLine
                                                                size={13}
                                                            />
                                                        )}
                                                    </button>
                                                    {/* Thumbs Down */}
                                                    <button
                                                        className={cn(
                                                            'thumbs-button transition-colors',
                                                            item.vote === false
                                                                ? 'text-gray-600 dark:text-gray-600'
                                                                : 'text-black hover:text-gray-600 dark:text-white'
                                                        )}
                                                        aria-label="thumbs down"
                                                        onClick={() => {
                                                            const nextVote =
                                                                item.vote ===
                                                                false
                                                                    ? null
                                                                    : false;
                                                            handleFeedback(
                                                                item.id,
                                                                item.idLog,
                                                                nextVote
                                                            );
                                                        }}
                                                    >
                                                        {item.vote === false ? (
                                                            <RiThumbDownFill
                                                                size={13}
                                                            />
                                                        ) : (
                                                            <RiThumbDownLine
                                                                size={13}
                                                            />
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    );
                                }

                                return (
                                    <div
                                        key={item.id}
                                        className={cn(
                                            'flex w-full flex-col items-center'
                                        )}
                                    >
                                        {/* Question */}
                                        <div
                                            className={cn(
                                                'ml-auto inline-flex max-w-[310px] items-center gap-2.5 self-end rounded-[22px] bg-[#F6F7F9] px-4 py-2 text-gray-800 dark:bg-gray-750 dark:text-[#E1E4E8]'
                                            )}
                                        >
                                            {renderQuestionText(item.question)}
                                        </div>
                                        {/* Answer/Thinking/Feedback */}
                                        {content}
                                        {!insertOnTop &&
                                            idx === arr.length - 1 && (
                                                <div ref={messagesEndRef} />
                                            )}
                                    </div>
                                );
                            }
                        )}
                    </div>
                )}
            </div>
            {/* Input + footer container */}
            <div className="flex w-full flex-shrink-0 flex-col items-center px-5 pb-3">
                {/* Input box */}
                <div
                    className={cn(
                        'flex w-full flex-shrink-0 flex-col justify-between rounded-[12px] border bg-white transition-colors duration-[180ms] dark:border-gray-700 dark:bg-gray-800',
                        isFocused
                            ? 'border-blue-500 dark:border-[#60A5FA]'
                            : 'border-[#C7CED6] dark:border-gray-700'
                    )}
                    style={{ height: 201, padding: '12px 12px 8px 12px' }}
                >
                    <textarea
                        ref={inputRef}
                        placeholder={t('aiAssistant.askAnything')}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        onKeyDown={handleTextareaKeyDown}
                        id="navia-ai-input"
                        className="font-roboto w-full flex-1 resize-none border-none bg-transparent text-[14px] leading-[1.4] text-[#3a444f] outline-none placeholder:text-[#78899C] dark:bg-transparent dark:text-gray-200 dark:placeholder:text-gray-500"
                        maxLength={400}
                        disabled={loading}
                    />
                    <button
                        type="button"
                        onClick={() => handleSubmit()}
                        aria-label={t('aiAssistant.sendButton')}
                        disabled={!input.trim() || loading}
                        className={cn(
                            'flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center self-end rounded-[12px] border-none transition-colors duration-200',
                            input.trim() && !loading
                                ? 'cursor-pointer bg-[#3A8DDE] hover:bg-[#2563eb] dark:bg-gray-900 dark:hover:bg-[#2D3B47]'
                                : 'cursor-default bg-[#E6E7EA] dark:bg-[#4B5563]'
                        )}
                    >
                        <RiArrowUpLine
                            size={17}
                            color={
                                input.trim() && !loading ? 'white' : '#000000'
                            }
                        />
                    </button>
                </div>
                {/* Footer */}
                <a
                    href="/ui/av/sobre/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-2.5 mt-5 flex items-center gap-1 text-[14px] text-[#185490] transition-colors hover:underline dark:text-[#B0D1F2]"
                >
                    <span>{t('aiAssistant.aboutLink')}</span>
                    <RiExternalLinkLine size={14} />
                </a>
            </div>
        </div>
    );
};

AiSidebar.displayName = 'AiSidebar';
export default AiSidebar;
